import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Divider,
  Alert,
  Snackbar,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import QuillEditor from '../components/QuillEditor';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import LanguageIcon from '@mui/icons-material/Language';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import RestoreIcon from '@mui/icons-material/Restore';
import Tooltip from '@mui/material/Tooltip';
import apiClient from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import DOMPurify from 'dompurify';

const Settings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  // Language selector for template display (fr/en) - local to this page, does not affect UI language
  const [templateLanguage, setTemplateLanguage] = useState<string>(i18n.language || 'fr');
  
  // SMS Settings
  const [smsEnabled, setSmsEnabled] = useState<boolean>(false);
  const [smsRecipients, setSmsRecipients] = useState<string[]>([]);
  const [newRecipient, setNewRecipient] = useState<string>('');
  const [recipientError, setRecipientError] = useState<string>('');
  const [isLoadingSms, setIsLoadingSms] = useState<boolean>(false);
  const [smsTemplate_fr, setSmsTemplate_fr] = useState<string>('');
  const [smsTemplate_en, setSmsTemplate_en] = useState<string>('');
  
  // Email Settings
  const [emailEnabled, setEmailEnabled] = useState<boolean>(false);
  const [emailSubject, setEmailSubject] = useState<string>('');
  const [emailTemplate, setEmailTemplate] = useState<string>('');
  const [emailSubject_fr, setEmailSubject_fr] = useState<string>('');
  const [emailTemplate_fr, setEmailTemplate_fr] = useState<string>('');
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [newEmailRecipient, setNewEmailRecipient] = useState<string>('');
  const [emailRecipientError, setEmailRecipientError] = useState<string>('');
  const [isLoadingEmail, setIsLoadingEmail] = useState<boolean>(false);

  // Global save state
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  // Heartbeat Recipients (Ingecom only)
  const [heartbeatRecipients, setHeartbeatRecipients] = useState<string[]>([]);
  const [newHeartbeatRecipient, setNewHeartbeatRecipient] = useState<string>('');

  // Battery Settings
  const [batteryThreshold, setBatteryThreshold] = useState<number>(2.5);
  const [batteryEnabled, setBatteryEnabled] = useState<boolean>(false);
  const [batteryRecipients, setBatteryRecipients] = useState<string[]>([]);
  const [batteryEmailSubject_en, setBatteryEmailSubject_en] = useState<string>('');
  const [batteryEmailTemplate_en, setBatteryEmailTemplate_en] = useState<string>('');
  const [batteryEmailSubject_fr, setBatteryEmailSubject_fr] = useState<string>('');
  const [batteryEmailTemplate_fr, setBatteryEmailTemplate_fr] = useState<string>('');
  const [newBatteryRecipient, setNewBatteryRecipient] = useState<string>('');
  const [batteryRecipientError, setBatteryRecipientError] = useState<string>('');

  // Flag touched for Quill Fields
  const [emailTemplateTouched, setEmailTemplateTouched] = useState(false);
  const [emailTemplateFrTouched, setEmailTemplateFrTouched] = useState(false);
  const [batteryEmailTemplateEnTouched, setBatteryEmailTemplateEnTouched] = useState(false);
  const [batteryEmailTemplateFrTouched, setBatteryEmailTemplateFrTouched] = useState(false);
  
  // Initial values to track changes - initialized as null until data is loaded
  const [initialValues, setInitialValues] = useState<any>(null);
  const [dataLoaded, setDataLoaded] = useState({
    sms: false,
    email: false,
    battery: false,
    language: false
  });

  const sameSortedArray = (a: string[] = [], b: string[] = []) =>
  JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());

  const normalizeString = (value: string | null | undefined) => value ?? '';

  const normalizeHtmlForCompare = (html: string | null | undefined): string => {
    if (!html || html === '<p><br></p>') {
      return '';
    }

    return html
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/>\s+</g, '><')
      .replace(/(<p><br><\/p>)+$/g, '')
      .trim();
  };

  const sanitizeHtmlForSave = (html: string | null | undefined): string => {
    if (!html || html === '<p><br></p>') {
      return '';
    }

  const normalized = html
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/>\s+</g, '><')
    .trim();

  return DOMPurify.sanitize(normalized, {
    ALLOWED_TAGS: [
      'h1', 'h2', 'h3',
      'p', 'br',
      'strong', 'b',
      'em', 'i',
      'u', 's',
      'ul', 'ol', 'li',
      'a', 'span'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel']
  });
};

  const handleEmailTemplateChange = (content: string, source?: string) => {
    setEmailTemplate(content);

    if (source === 'user') {
      setEmailTemplateTouched(true);
    }
  };

  const handleEmailTemplateFrChange = (content: string, source?: string) => {
    setEmailTemplate_fr(content);

    if (source === 'user') {
      setEmailTemplateFrTouched(true);
    }
  };

  const handleBatteryEmailTemplateEnChange = (content: string, source?: string) => {
    setBatteryEmailTemplate_en(content);

    if (source === 'user') {
      setBatteryEmailTemplateEnTouched(true);
    }
  };

  const handleBatteryEmailTemplateFrChange = (content: string, source?: string) => {
    setBatteryEmailTemplate_fr(content);

    if (source === 'user') {
      setBatteryEmailTemplateFrTouched(true);
    }
  };

  // Check for unsaved changes - only after all data is loaded
  const checkForChanges = useCallback(() => {
    // Don't check for changes until all data is loaded
    if (!initialValues || !dataLoaded.sms || !dataLoaded.email || !dataLoaded.battery || !dataLoaded.language) {
      setHasUnsavedChanges(false);
      return;
    }

    const currentValues = {
      smsEnabled,
      smsRecipients,
      smsTemplate_fr,
      smsTemplate_en,
      emailEnabled,
      emailSubject,
      emailTemplate,
      emailSubject_fr,
      emailTemplate_fr,
      emailRecipients,
      heartbeatRecipients,
      batteryThreshold,
      batteryEnabled,
      batteryRecipients,
      batteryEmailSubject_en,
      batteryEmailTemplate_en,
      batteryEmailSubject_fr,
      batteryEmailTemplate_fr,
      templateLanguage,
      initialValues,
      dataLoaded,
      emailTemplateTouched,
      emailTemplateFrTouched,
      batteryEmailTemplateEnTouched,
      batteryEmailTemplateFrTouched
    };

    const comparisons = {
      smsEnabled: currentValues.smsEnabled !== initialValues.smsEnabled,
      smsRecipients: !sameSortedArray(currentValues.smsRecipients, initialValues.smsRecipients),
      smsTemplate_fr: normalizeString(currentValues.smsTemplate_fr) !== normalizeString(initialValues.smsTemplate_fr),
      smsTemplate_en: normalizeString(currentValues.smsTemplate_en) !== normalizeString(initialValues.smsTemplate_en),

      emailEnabled: currentValues.emailEnabled !== initialValues.emailEnabled,
      emailSubject: normalizeString(currentValues.emailSubject) !== normalizeString(initialValues.emailSubject),

      emailTemplate:
        emailTemplateTouched &&
        normalizeHtmlForCompare(currentValues.emailTemplate) !==
          normalizeHtmlForCompare(initialValues.emailTemplate),

      emailSubject_fr: normalizeString(currentValues.emailSubject_fr) !== normalizeString(initialValues.emailSubject_fr),

      emailTemplate_fr:
        emailTemplateFrTouched &&
        normalizeHtmlForCompare(currentValues.emailTemplate_fr) !==
          normalizeHtmlForCompare(initialValues.emailTemplate_fr),

      emailRecipients: !sameSortedArray(currentValues.emailRecipients, initialValues.emailRecipients),
      heartbeatRecipients: !sameSortedArray(currentValues.heartbeatRecipients, initialValues.heartbeatRecipients),

      batteryThreshold: currentValues.batteryThreshold !== initialValues.batteryThreshold,
      batteryEnabled: currentValues.batteryEnabled !== initialValues.batteryEnabled,
      batteryRecipients: !sameSortedArray(currentValues.batteryRecipients, initialValues.batteryRecipients),

      batteryEmailSubject_en:
        normalizeString(currentValues.batteryEmailSubject_en) !==
          normalizeString(initialValues.batteryEmailSubject_en),

      batteryEmailTemplate_en:
        batteryEmailTemplateEnTouched &&
        normalizeHtmlForCompare(currentValues.batteryEmailTemplate_en) !==
          normalizeHtmlForCompare(initialValues.batteryEmailTemplate_en),

      batteryEmailSubject_fr:
        normalizeString(currentValues.batteryEmailSubject_fr) !==
          normalizeString(initialValues.batteryEmailSubject_fr),

      batteryEmailTemplate_fr:
        batteryEmailTemplateFrTouched &&
        normalizeHtmlForCompare(currentValues.batteryEmailTemplate_fr) !==
          normalizeHtmlForCompare(initialValues.batteryEmailTemplate_fr),

      templateLanguage: currentValues.templateLanguage !== initialValues.templateLanguage,
    };

    const hasChanges = Object.values(comparisons).some(Boolean);

    setHasUnsavedChanges(hasChanges);
  }, [smsEnabled, smsRecipients, smsTemplate_fr, smsTemplate_en, emailEnabled, emailSubject, emailTemplate, emailSubject_fr, emailTemplate_fr, emailRecipients, heartbeatRecipients, batteryEnabled, batteryThreshold, batteryRecipients, batteryEmailSubject_en, batteryEmailTemplate_en, batteryEmailSubject_fr, batteryEmailTemplate_fr, templateLanguage, initialValues, dataLoaded, emailTemplateTouched, emailTemplateFrTouched, batteryEmailTemplateEnTouched, batteryEmailTemplateFrTouched]);

  // Check for changes whenever relevant state changes
  useEffect(() => {
    checkForChanges();
  }, [checkForChanges]);

  // Initialize initial values only after all data is loaded
  useEffect(() => {
    if (dataLoaded.sms && dataLoaded.email && dataLoaded.battery && dataLoaded.language && !initialValues) {
      const initialData = {
        smsEnabled,
        smsRecipients: [...smsRecipients],
        smsTemplate_fr,
        smsTemplate_en,
        emailEnabled,
        emailSubject,
        emailTemplate,
        emailSubject_fr,
        emailTemplate_fr,
        emailRecipients: [...emailRecipients],
        heartbeatRecipients: [...heartbeatRecipients],
        batteryThreshold,
        batteryEnabled,
        batteryRecipients: [...batteryRecipients],
        batteryEmailSubject_en,
        batteryEmailTemplate_en,
        batteryEmailSubject_fr,
        batteryEmailTemplate_fr,
        templateLanguage
      };


      setInitialValues(initialData);
      setEmailTemplateTouched(false);
      setEmailTemplateFrTouched(false);
      setBatteryEmailTemplateEnTouched(false);
      setBatteryEmailTemplateFrTouched(false);
    }
  }, [dataLoaded, smsEnabled, smsRecipients, smsTemplate_fr, smsTemplate_en, emailEnabled, emailSubject, emailTemplate, emailSubject_fr, emailTemplate_fr, emailRecipients, heartbeatRecipients, batteryThreshold, batteryEnabled, batteryRecipients, batteryEmailSubject_en, batteryEmailTemplate_en, batteryEmailSubject_fr, batteryEmailTemplate_fr, templateLanguage, initialValues]);

  // Intercept browser navigation (back/forward buttons)
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        // Push the current state back to prevent navigation
        window.history.pushState(null, '', location.pathname);
        showNavigationConfirmation(() => {
          setHasUnsavedChanges(false);
          window.history.back();
        });
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('popstate', handlePopState);
      // Push a dummy state to enable popstate detection
      window.history.pushState(null, '', location.pathname);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges]);
  
  // Intercept navigation links
  useEffect(() => {
    const handleLinkClick = (e: MouseEvent) => {
      if (hasUnsavedChanges) {
        const target = e.target as HTMLElement;
        const linkElement = target.closest('a[href]') as HTMLAnchorElement;
        
        if (linkElement && linkElement.href) {
          // Check if it's an internal navigation link
          const currentOrigin = window.location.origin;
          const linkUrl = new URL(linkElement.href, currentOrigin);
          
          if (linkUrl.origin === currentOrigin && linkUrl.pathname !== location.pathname) {
            e.preventDefault();
            e.stopPropagation();
            
            showNavigationConfirmation(() => {
              setHasUnsavedChanges(false);
              // Use React Router navigation instead of window.location.href
              const path = linkElement.pathname;
              navigate(path);
            });
          }
        }
      }
    };

    if (hasUnsavedChanges) {
      document.addEventListener('click', handleLinkClick, true);
    }

    return () => {
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, [hasUnsavedChanges]);
  
  // Load template language preference from backend
  useEffect(() => {
    const loadTemplateLanguage = async () => {
      try {
        const response = await apiClient.get('/settings/language');
        if (response.data?.language) {
          setTemplateLanguage(response.data.language);
        }
      } catch (error) {
        console.error('Failed to load template language preference:', error);
      } finally {
        setDataLoaded(prev => ({ ...prev, language: true }));
      }
    };
    loadTemplateLanguage();
  }, []);

  // Load SMS settings
  useEffect(() => {
    const loadSmsSettings = async () => {
      try {
        setIsLoadingSms(true);
        const response = await apiClient.get('/settings/sms');
        const smsData = {
          enabled: response.data.enabled,
          recipients: response.data.recipients || [],
          sms_template_fr: response.data.sms_template_fr || 'Alerte: {sensor_name}\n({location})\n{event_type}\nNiveau: {alert_level}\nMap: {google_map}',
          sms_template_en: response.data.sms_template_en || 'Alert: {sensor_name}\n({location})\n{event_type}\nLevel: {alert_level}\nMap: {google_map}'
        };
        setSmsEnabled(smsData.enabled);
        setSmsRecipients(smsData.recipients);
        setSmsTemplate_fr(smsData.sms_template_fr);
        setSmsTemplate_en(smsData.sms_template_en);
        
        // Mark SMS data as loaded
        setDataLoaded(prev => ({ ...prev, sms: true }));
      } catch (error) {
        console.error('Failed to load SMS settings:', error);
      } finally {
        setIsLoadingSms(false);
      }
    };
    
    loadSmsSettings();
  }, []);
  
  // Load Email settings
  useEffect(() => {
    const loadEmailSettings = async () => {
      try {
        setIsLoadingEmail(true);
        const response = await apiClient.get('/settings/email');
        const data = response.data;
        const emailData = {
          enabled: data.enabled,
          subject: data.email_subject_en || '',
          template: data.email_template_en || '',
          subject_fr: data.email_subject_fr || '',
          template_fr: data.email_template_fr || '',
          recipients: data.recipients || [],
          heartbeat_recipients: data.heartbeat_recipients || []
        };
        setEmailEnabled(emailData.enabled);
        setEmailSubject(emailData.subject);
        setEmailTemplate(emailData.template);
        setEmailSubject_fr(emailData.subject_fr);
        setEmailTemplate_fr(emailData.template_fr);
        setEmailRecipients(emailData.recipients);
        setHeartbeatRecipients(emailData.heartbeat_recipients);
        
        // Mark Email data as loaded
        setDataLoaded(prev => ({ ...prev, email: true }));
      } catch (error) {
        console.error('Failed to load Email settings:', error);
      } finally {
        setIsLoadingEmail(false);
      }
    };
    
    loadEmailSettings();
  }, []);

  // Load Battery settings
  useEffect(() => {
    const loadBatterySettings = async () => {
      try {
        // First check if the battery endpoint exists
        try {
          const response = await apiClient.get('/settings/battery');
          const threshold = response.data?.threshold ?? 2.5;
          const recipients = response.data?.recipients ?? [];
          const enabled = response.data?.enabled ?? false;
          const email_subject_en = response.data?.email_subject_en ?? 'Low Battery Notification';
          const email_template_en = response.data?.email_template_en ?? '';
          const email_subject_fr = response.data?.email_subject_fr ?? 'Batterie Faible Notification';
          const email_template_fr = response.data?.email_template_fr ?? '';

          setBatteryThreshold(threshold);
          setBatteryRecipients(recipients);
          setBatteryEnabled(enabled);
          setBatteryEmailSubject_en(email_subject_en);
          setBatteryEmailTemplate_en(email_template_en);
          setBatteryEmailSubject_fr(email_subject_fr);
          setBatteryEmailTemplate_fr(email_template_fr);

          // Mark Battery data as loaded
          setDataLoaded(prev => ({ ...prev, battery: true }));
        } catch (error) {
          console.error('Battery endpoint might not exist:', error);
          // Mark as loaded even if endpoint doesn't exist
          setDataLoaded(prev => ({ ...prev, battery: true }));
        }
      } catch (error) {
        console.error('Failed to load battery settings:', error);
      }
    };
    loadBatterySettings();
  }, []);

  const removeBatteryRecipient = (email: string) => {
    setBatteryRecipients(batteryRecipients.filter(r => r !== email));
  };

  // Save all settings
  const handleSaveAllSettings = async () => {
    try {
      setIsSaving(true);

      const sanitizedEmailTemplate = sanitizeHtmlForSave(emailTemplate);
      const sanitizedEmailTemplateFr = sanitizeHtmlForSave(emailTemplate_fr);
      const sanitizedBatteryEmailTemplateEn = sanitizeHtmlForSave(batteryEmailTemplate_en);
      const sanitizedBatteryEmailTemplateFr = sanitizeHtmlForSave(batteryEmailTemplate_fr);

      // Save template language preference (determines which template is sent by email/SMS)
      try {
        await apiClient.post('/settings/language', { language: templateLanguage });
      } catch (langError) {
        console.error('Error saving template language preference:', langError);
      }
      
      // Save SMS settings
      try {
        await apiClient.put('/settings/sms', {
          enabled: smsEnabled,
          recipients: smsRecipients,
          sms_template_fr: smsTemplate_fr,
          sms_template_en: smsTemplate_en
        });
      } catch (smsError) {
        console.error('Error saving SMS settings:', smsError);
      }
      
      // Save email settings (both languages)
      try {
        await apiClient.put('/settings/email', {
          enabled: emailEnabled,
          recipients: emailRecipients,
          heartbeat_recipients: heartbeatRecipients,
          email_subject_en: emailSubject,
          email_template_en: sanitizedEmailTemplate,
          email_subject_fr: emailSubject_fr,
          email_template_fr: sanitizedEmailTemplateFr
        });
      } catch (emailError) {
        console.error('Error saving email settings:', emailError);
      }
      
      // Save battery settings (both languages)
      try {
        await apiClient.put('/settings/battery', {
          enabled: batteryEnabled,
          threshold: batteryThreshold,
          recipients: batteryRecipients,
          email_subject_en: batteryEmailSubject_en,
          email_template_en: sanitizedBatteryEmailTemplateEn,
          email_subject_fr: batteryEmailSubject_fr,
          email_template_fr: sanitizedBatteryEmailTemplateFr
        });
      } catch (batteryError) {
        console.error('Error saving battery settings:', batteryError);
      }
      
      // Update initial values to current values
      setInitialValues({
        smsEnabled,
        smsRecipients: [...smsRecipients],
        smsTemplate_fr,
        smsTemplate_en,
        emailEnabled,
        emailSubject,
        emailTemplate: sanitizedEmailTemplate,
        emailSubject_fr,
        emailTemplate_fr: sanitizedEmailTemplateFr,
        emailRecipients: [...emailRecipients],
        heartbeatRecipients: [...heartbeatRecipients],
        batteryThreshold,
        batteryEnabled,
        batteryRecipients: [...batteryRecipients],
        batteryEmailSubject_en,
        batteryEmailTemplate_en: sanitizedBatteryEmailTemplateEn,
        batteryEmailSubject_fr,
        batteryEmailTemplate_fr: sanitizedBatteryEmailTemplateFr,
        templateLanguage
      });

      setEmailTemplateTouched(false);
      setEmailTemplateFrTouched(false);
      setBatteryEmailTemplateEnTouched(false);
      setBatteryEmailTemplateFrTouched(false);

      setHasUnsavedChanges(false);
      setSaveSuccess(true);
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle navigation confirmation
  const handleNavigationConfirm = () => {
    setShowConfirmDialog(false);
    setHasUnsavedChanges(false);
    if (pendingNavigation) {
      pendingNavigation();
    }
  };

  const handleNavigationCancel = () => {
    setShowConfirmDialog(false);
  };

  // Handle save and navigate
  const handleSaveAndNavigate = async () => {
    let saveSuccess = false;
    
    try {
      setIsSaving(true);

      const sanitizedEmailTemplate = sanitizeHtmlForSave(emailTemplate);
      const sanitizedEmailTemplateFr = sanitizeHtmlForSave(emailTemplate_fr);
      const sanitizedBatteryEmailTemplateEn = sanitizeHtmlForSave(batteryEmailTemplate_en);
      const sanitizedBatteryEmailTemplateFr = sanitizeHtmlForSave(batteryEmailTemplate_fr);

      // Save template language preference (determines which template is sent by email/SMS)
      try {
        await apiClient.post('/settings/language', { language: templateLanguage });
      } catch (langError) {
        console.error('Error saving template language preference:', langError);
      }

      // Save SMS settings
      try {
        await apiClient.put('/settings/sms', {
          enabled: smsEnabled,
          recipients: smsRecipients,
          sms_template_fr: smsTemplate_fr,
          sms_template_en: smsTemplate_en
        });
      } catch (smsError) {
        console.error('Error saving SMS settings:', smsError);
      }
      
      // Save email settings (both languages)
      try {
        await apiClient.put('/settings/email', {
          enabled: emailEnabled,
          recipients: emailRecipients,
          heartbeat_recipients: heartbeatRecipients,
          email_subject_en: emailSubject,
          email_template_en: sanitizedEmailTemplate,
          email_subject_fr: emailSubject_fr,
          email_template_fr: sanitizedEmailTemplateFr
        });
      } catch (emailError) {
        console.error('Error saving email settings:', emailError);
      }
      
      // Save battery settings (both languages)
      try {
        await apiClient.put('/settings/battery', {
          enabled: batteryEnabled,
          threshold: batteryThreshold,
          recipients: batteryRecipients,
          email_subject_en: batteryEmailSubject_en,
          email_template_en: sanitizedBatteryEmailTemplateEn,
          email_subject_fr: batteryEmailSubject_fr,
          email_template_fr: sanitizedBatteryEmailTemplateFr
        });
      } catch (batteryError) {
        console.error('Error saving battery settings:', batteryError);
      }

      // Update initial values to current values
      setInitialValues({
        smsEnabled,
        smsRecipients: [...smsRecipients],
        smsTemplate_fr,
        smsTemplate_en,
        emailEnabled,
        emailSubject,
        emailTemplate: sanitizedEmailTemplate,
        emailSubject_fr,
        emailTemplate_fr: sanitizedEmailTemplateFr,
        emailRecipients: [...emailRecipients],
        heartbeatRecipients: [...heartbeatRecipients],
        batteryThreshold,
        batteryEnabled,
        batteryRecipients: [...batteryRecipients],
        batteryEmailSubject_en,
        batteryEmailTemplate_en: sanitizedBatteryEmailTemplateEn,
        batteryEmailSubject_fr,
        batteryEmailTemplate_fr: sanitizedBatteryEmailTemplateFr,
        templateLanguage
      });

       setEmailTemplateTouched(false);
      setEmailTemplateFrTouched(false);
      setBatteryEmailTemplateEnTouched(false);
      setBatteryEmailTemplateFrTouched(false);

      setHasUnsavedChanges(false);
      setSaveSuccess(true);
      saveSuccess = true;

      // After successful save, proceed with navigation
      if (pendingNavigation) {
        pendingNavigation();
      }
      setShowConfirmDialog(false);
    } catch (error) {
      console.error('Failed to save before navigation:', error);
      // Keep dialog open if save fails
    } finally {
      setIsSaving(false);
      
      // Ensure navigation happens even if save fails
      if (!saveSuccess && pendingNavigation) {
        pendingNavigation();
        setShowConfirmDialog(false);
      }
    }
  };

  // Show navigation confirmation dialog
  const showNavigationConfirmation = (navigateFn: () => void) => {
    setPendingNavigation(() => navigateFn);
    setShowConfirmDialog(true);
  };

  // Close success message
  const handleCloseSuccessMessage = () => {
    setSaveSuccess(false);
  };

  // SMS Settings handlers
  const handleSmsEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSmsEnabled(event.target.checked);
  };
  
  const validatePhoneNumber = (number: string): boolean => {
    // Basic phone number validation - can be enhanced
    const phoneRegex = /^\d{6,15}$/;
    return phoneRegex.test(number.trim());
  };
  
  const handleAddRecipient = () => {
    const trimmedNumber = newRecipient.trim();
    
    if (!trimmedNumber) {
      setRecipientError(t('settings.sms.errors.emptyNumber') as string);
      return;
    }
    
    if (!validatePhoneNumber(trimmedNumber)) {
      setRecipientError(t('settings.sms.errors.invalidNumber') as string);
      return;
    }
    
    if (smsRecipients.includes(trimmedNumber)) {
      setRecipientError(t('settings.sms.errors.duplicateNumber') as string);
      return;
    }
    
    setSmsRecipients([...smsRecipients, trimmedNumber]);
    setNewRecipient('');
    setRecipientError('');
  };
  
  const handleRemoveRecipient = (number: string) => {
    setSmsRecipients(smsRecipients.filter(n => n !== number));
  };
  
  const handleNewRecipientChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewRecipient(e.target.value);
    if (recipientError) setRecipientError('');
  };
  
  const handleNewRecipientKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddRecipient();
    }
  };
  
  // Email Settings handlers
  const handleEmailEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailEnabled(event.target.checked);
  };
  
  const validateEmail = (email: string): boolean => {
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };
  
  // Battery Settings handlers
  const handleBatteryEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBatteryEnabled(event.target.checked);
  };

  // Validation d'email pour les alertes batterie
  const handleAddBatteryRecipient = () => {
    const trimmedEmail = newBatteryRecipient.trim();
    
    if (!trimmedEmail) {
      setBatteryRecipientError(t('settings.email.errors.emptyEmail') as string);
      return;
    }
    
    if (!validateEmail(trimmedEmail)) {
      setBatteryRecipientError(t('settings.email.errors.invalidEmail') as string);
      return;
    }
    
    if (batteryRecipients.includes(trimmedEmail)) {
      setBatteryRecipientError(t('settings.email.errors.duplicateEmail') as string);
      return;
    }
    
    setBatteryRecipients([...batteryRecipients, trimmedEmail]);
    setNewBatteryRecipient('');
    setBatteryRecipientError('');
  };
  
  const handleNewBatteryRecipientChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewBatteryRecipient(e.target.value);
    if (batteryRecipientError) setBatteryRecipientError('');
  };
  
  const handleNewBatteryRecipientKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddBatteryRecipient();
    }
  };
  
  const handleAddEmailRecipient = () => {
    const trimmedEmail = newEmailRecipient.trim();
    
    if (!trimmedEmail) {
      setEmailRecipientError(t('settings.email.errors.emptyEmail') as string);
      return;
    }
    
    if (!validateEmail(trimmedEmail)) {
      setEmailRecipientError(t('settings.email.errors.invalidEmail') as string);
      return;
    }
    
    if (emailRecipients.includes(trimmedEmail)) {
      setEmailRecipientError(t('settings.email.errors.duplicateEmail') as string);
      return;
    }
    
    setEmailRecipients([...emailRecipients, trimmedEmail]);
    setNewEmailRecipient('');
    setEmailRecipientError('');
  };
  
  const handleRemoveEmailRecipient = (email: string) => {
    setEmailRecipients(emailRecipients.filter(e => e !== email));
  };
  
  const handleNewEmailRecipientChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewEmailRecipient(e.target.value);
    if (emailRecipientError) setEmailRecipientError('');
  };
  
  const handleNewEmailRecipientKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmailRecipient();
    }
  };

  // Default SMS templates (static, no backend URL needed)
  const DEFAULT_SMS_TEMPLATE_FR = 'Alerte: {sensor_name}\n({location})\n{event_type}\nNiveau: {alert_level}\nMap: {google_map}';
  const DEFAULT_SMS_TEMPLATE_EN = 'Alert: {sensor_name}\n({location})\n{event_type}\nLevel: {alert_level}\nMap: {google_map}';

  const handleResetSmsTemplate = () => {
    if (templateLanguage === 'en') {
      setSmsTemplate_en(DEFAULT_SMS_TEMPLATE_EN);
    } else {
      setSmsTemplate_fr(DEFAULT_SMS_TEMPLATE_FR);
    }
  };

  const handleResetEmailTemplate = async () => {
    try {
      const response = await apiClient.get('/settings/email/defaults');
      const data = response.data;
      if (templateLanguage === 'en') {
        setEmailSubject(data.email_subject_en);
        setEmailTemplate(data.email_template_en);
        setEmailTemplateTouched(true);
      } else {
        setEmailSubject_fr(data.email_subject_fr);
        setEmailTemplate_fr(data.email_template_fr);
        setEmailTemplateFrTouched(true);
      }
    } catch (error) {
      console.error('Failed to load email defaults:', error);
    }
  };

  const handleResetBatteryEmailTemplate = async () => {
    try {
      const response = await apiClient.get('/settings/battery/defaults');
      const data = response.data;
      if (templateLanguage === 'en') {
        setBatteryEmailSubject_en(data.email_subject_en);
        setBatteryEmailTemplate_en(data.email_template_en);
        setBatteryEmailTemplateEnTouched(true);
      } else {
        setBatteryEmailSubject_fr(data.email_subject_fr);
        setBatteryEmailTemplate_fr(data.email_template_fr);
        setBatteryEmailTemplateFrTouched(true);
      }
    } catch (error) {
      console.error('Failed to load battery email defaults:', error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('settings.title')}
      </Typography>

      {/* Template Language Selector */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LanguageIcon sx={{ fontSize: 28, mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">{t('settings.language')}</Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="template-language-select-label">
                {t('settings.languageLabel')}
              </InputLabel>
              <Select
                labelId="template-language-select-label"
                id="template-language-select"
                value={templateLanguage}
                label={t('settings.languageLabel')}
                onChange={(e) => setTemplateLanguage(e.target.value as string)}
              >
                <MenuItem value="fr">{t('settings.french')}</MenuItem>
                <MenuItem value="en">{t('settings.english')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* SMS Notification Settings */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <NotificationsIcon sx={{ fontSize: 28, mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">{t('settings.sms.title')}</Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
          </Grid>

          {isLoadingSms ? (
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Grid>
          ) : (
            <>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={smsEnabled} 
                      onChange={handleSmsEnabledChange}
                      color="primary"
                    />
                  }
                  label={t('settings.sms.enable')}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('settings.sms.description')}
                </Typography>
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('settings.sms.recipients')}
                </Typography>
                
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <TextField
                    label={t('settings.sms.addRecipient')}
                    variant="outlined"
                    value={newRecipient}
                    onChange={handleNewRecipientChange}
                    onKeyPress={handleNewRecipientKeyPress}
                    error={!!recipientError}
                    helperText={recipientError}
                    fullWidth
                    disabled={!smsEnabled}
                    placeholder={t('settings.sms.phonePlaceholder') as string}
                    sx={{ mr: 1 }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddRecipient}
                    disabled={!smsEnabled || !newRecipient.trim()}
                    sx={{ minWidth: '120px' }}
                  >
                    {t('common.add')}
                  </Button>
                </Box>
                
                <Box sx={{ mt: 2 }}>
                  {smsRecipients.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('settings.sms.noRecipients')}
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {smsRecipients.map((number) => (
                        <Chip
                          key={number}
                          label={number}
                          onDelete={() => handleRemoveRecipient(number)}
                          deleteIcon={<DeleteIcon />}
                          disabled={!smsEnabled}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* SMS Content Section */}
              <Grid item xs={12} sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle1">
                    {t('settings.sms.content.title')}
                  </Typography>
                  <Tooltip title={t('settings.sms.content.resetToDefaultTooltip') as string}>
                    <span>
                      <Button
                        size="small"
                        startIcon={<RestoreIcon />}
                        onClick={handleResetSmsTemplate}
                        disabled={!smsEnabled}
                        variant="outlined"
                        color="secondary"
                      >
                        {t('settings.sms.content.resetToDefault')}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                
                <TextField
                  label={t('settings.sms.content.template')}
                  variant="outlined"
                  value={templateLanguage === 'en' ? smsTemplate_en : smsTemplate_fr}
                  onChange={(e) => {
                    if (templateLanguage === 'en') {
                      setSmsTemplate_en(e.target.value);
                    } else {
                      setSmsTemplate_fr(e.target.value);
                    }
                  }}
                  fullWidth
                  disabled={!smsEnabled}
                  placeholder={t('settings.sms.content.templatePlaceholder') as string}
                  multiline
                  rows={3}
                  sx={{ mb: 2 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('settings.sms.content.templateHelp')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('settings.sms.content.placeholdersHelp')}
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Email Notification Settings */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <NotificationsIcon sx={{ fontSize: 28, mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">{t('settings.email.title')}</Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
          </Grid>

          {isLoadingEmail ? (
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Grid>
          ) : (
            <>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={emailEnabled} 
                      onChange={handleEmailEnabledChange}
                      color="primary"
                    />
                  }
                  label={t('settings.email.enable')}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('settings.email.description')}
                </Typography>
              </Grid>

            {/* Email Content Section */}
            <Grid item xs={12} sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1">
                  {t('settings.email.content.title')}
                </Typography>
                <Tooltip title={t('settings.email.content.resetToDefaultTooltip') as string}>
                  <span>
                    <Button
                      size="small"
                      startIcon={<RestoreIcon />}
                      onClick={handleResetEmailTemplate}
                      disabled={!emailEnabled}
                      variant="outlined"
                      color="secondary"
                    >
                      {t('settings.email.content.resetToDefault')}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              
              <TextField
                label={t('settings.email.content.subject')}
                variant="outlined"
                value={templateLanguage === 'en' ? emailSubject : emailSubject_fr}
                onChange={(e) => {
                  if (templateLanguage === 'en') {
                    setEmailSubject(e.target.value);
                  } else {
                    setEmailSubject_fr(e.target.value);
                  }
                }}
                fullWidth
                disabled={!emailEnabled}
                placeholder={t('settings.email.content.subjectPlaceholder') as string}
                sx={{ mb: 2 }}
              />
              
              <Typography variant="subtitle2" gutterBottom>
                {t('settings.email.content.template')}
              </Typography>
              <Box sx={{ mb: 2, '.ql-container': { height: '200px' }, '.ql-editor': { minHeight: '150px' } }}>
                <QuillEditor
                  value={templateLanguage === 'en' ? emailTemplate : emailTemplate_fr}
                  onChange={(content: string, source?: string) => {
                    if (templateLanguage === 'en') {
                      handleEmailTemplateChange(content, source);
                    } else {
                      handleEmailTemplateFrChange(content, source);
                    }
                  }}
                  readOnly={!emailEnabled}
                  placeholder={t('settings.email.content.templatePlaceholder') as string}
                  theme="snow"
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, 3, false] }], 
                      ['bold', 'italic', 'underline', 'strike'], 
                      [{ color: [] }, { background: [] }], 
                      [{ list: 'ordered' }, { list: 'bullet' }], 
                      ['link', 'image'],
                      ['clean'], 
                    ],
                  }}
                  key={`email-editor-${templateLanguage}`}
                />
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('settings.email.content.templateHelp')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('settings.email.content.placeholdersHelp')}
              </Typography>
            </Grid>

            {/* Email Recipients Section */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t('settings.email.recipients')}
              </Typography>
              
              <Box sx={{ display: 'flex', mb: 2 }}>
                <TextField
                  label={t('settings.email.addRecipient')}
                  variant="outlined"
                  value={newEmailRecipient}
                  onChange={handleNewEmailRecipientChange}
                  onKeyPress={handleNewEmailRecipientKeyPress}
                  error={!!emailRecipientError}
                  helperText={emailRecipientError}
                  fullWidth
                  disabled={!emailEnabled}
                  placeholder={t('settings.email.emailPlaceholder') as string}
                  sx={{ mr: 1 }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddEmailRecipient}
                  disabled={!emailEnabled || !newEmailRecipient.trim()}
                  sx={{ minWidth: '120px' }}
                >
                  {t('common.add')}
                </Button>
              </Box>
              
              <Box sx={{ mt: 2 }}>
                {emailRecipients.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.email.noRecipients')}
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {emailRecipients.map((email) => (
                      <Chip
                        key={email}
                        label={email}
                        onDelete={() => handleRemoveEmailRecipient(email)}
                        deleteIcon={<DeleteIcon />}
                        disabled={!emailEnabled}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>
          </>
        )}
        </Grid>
    </Paper>

      {/* Battery Settings */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BatteryFullIcon sx={{ fontSize: 28, mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">{t('settings.battery.settings')}</Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
          </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label={t('settings.battery.threshold')}
                variant="outlined"
                type="number"
                value={batteryThreshold}
                onChange={(e) => {
                  let value = Number(e.target.value);
                  if (value < 0) value = 0;
                  if (value > 5) value = 5;
                  setBatteryThreshold(value);
                }}
                InputProps={{
                  endAdornment: <span>V</span>,
                  inputProps: { min: 0, max: 5, step: 0.01 }
                }}
                fullWidth
                sx={{ mb: 0.5 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0 }}>
                {t('settings.battery.thresholdHelper')}
              </Typography>
            </Grid>

            <Grid item xs={12} sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={batteryEnabled}
                      onChange={handleBatteryEnabledChange}
                      color="primary"
                    />
                  }
                  label={t('settings.battery.enable')}
                />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('settings.battery.enableBatteryNotifications')}
              </Typography>
            </Grid>

            {/* Battery Email Content Section */}
            <Grid item xs={12} sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1">
                  {t('settings.email.content.title')}
                </Typography>
                <Tooltip title={t('settings.email.content.resetToDefaultTooltip') as string}>
                  <span>
                    <Button
                      size="small"
                      startIcon={<RestoreIcon />}
                      onClick={handleResetBatteryEmailTemplate}
                      disabled={!batteryEnabled}
                      variant="outlined"
                      color="secondary"
                    >
                      {t('settings.email.content.resetToDefault')}
                    </Button>
                  </span>
                </Tooltip>
              </Box>

              <TextField
                label={t('settings.email.content.subject')}
                variant="outlined"
                value={templateLanguage === 'en' ? batteryEmailSubject_en : batteryEmailSubject_fr}
                onChange={(e) => {
                  if (templateLanguage === 'en') {
                    setBatteryEmailSubject_en(e.target.value);
                  } else {
                    setBatteryEmailSubject_fr(e.target.value);
                  }
                }}
                fullWidth
                disabled={!batteryEnabled}
                placeholder={t('settings.email.content.subjectPlaceholder') as string}
                sx={{ mb: 2 }}
              />

              <Typography variant="subtitle2" gutterBottom>
                {t('settings.email.content.template')}
              </Typography>
              <Box sx={{ mb: 2, '.ql-container': { height: '200px' }, '.ql-editor': { minHeight: '150px' } }}>
                <QuillEditor
                  value={templateLanguage === 'en' ? batteryEmailTemplate_en : batteryEmailTemplate_fr}
                  onChange={(content: string, source?: string) => {
                    if (templateLanguage === 'en') {
                      handleBatteryEmailTemplateEnChange(content, source);
                    } else {
                      handleBatteryEmailTemplateFrChange(content, source);
                    }
                  }}
                  readOnly={!batteryEnabled}
                  placeholder={t('settings.email.content.templatePlaceholder') as string}
                  theme="snow"
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ color: [] }, { background: [] }],
                      [{ list: 'ordered' }, { list: 'bullet' }],
                      ['link', 'image'],
                      ['clean'],
                    ],
                  }}
                  key={`battery-editor-${templateLanguage}`}
                />
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('settings.email.content.templateHelp')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('settings.email.content.placeholdersHelp')}
              </Typography>
            </Grid>

            {/* Ajout d'un email pour notifications batterie faible */}
            <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('settings.battery.lowNotificationRecipients')}
            </Typography>

            {/* Champ pour ajouter un destinataire */}
            <Box sx={{ display: 'flex', mb: 2 }}>
              <TextField
                  label={t('settings.battery.newBatteryLowRecipient')}
                  variant="outlined"
                  value={newBatteryRecipient}
                  onChange={handleNewBatteryRecipientChange}
                  onKeyPress={handleNewBatteryRecipientKeyPress}
                  error={!!batteryRecipientError}
                  helperText={batteryRecipientError}
                  fullWidth
                  disabled={!batteryEnabled}
                  placeholder={t('settings.battery.lowRecipientPlaceholder') as string}
                  sx={{ mr: 1 }}
                  />
                  <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddBatteryRecipient}
                  disabled={!batteryEnabled || !newBatteryRecipient.trim()}
                  sx={{ minWidth: '120px' }}
                >
                {t('common.add')}
              </Button>
            </Box>

            {/* Liste des destinataires */}
            <Box sx={{ mt: 2 }}>
              {batteryRecipients.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('settings.battery.noBatteryLowRecipients')}
              </Typography>
              ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {batteryRecipients.map((email) => (
                <Chip
                  key={email}
                  label={email}
                  onDelete={() => removeBatteryRecipient(email)}
                  deleteIcon={<DeleteIcon />}
                  disabled={!batteryEnabled}
                />
                ))}
              </Box>
              )}
            </Box>
            </Grid>
        </Grid>
      </Paper>

      {/* Heartbeat Alert Recipients - Ingecom only */}
      {user?.role === 'ingecom' && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">{t('settings.heartbeatRecipients.settings')}</Typography>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('settings.heartbeatRecipients.description')}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                {t('settings.heartbeatRecipients.recipients')}
              </Typography>

              <Box sx={{ display: 'flex', mb: 2 }}>
                <TextField
                  label={t('settings.heartbeatRecipients.newRecipient')}
                  variant="outlined"
                  value={newHeartbeatRecipient}
                  onChange={(e) => setNewHeartbeatRecipient(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newHeartbeatRecipient && !heartbeatRecipients.includes(newHeartbeatRecipient)) {
                        setHeartbeatRecipients([...heartbeatRecipients, newHeartbeatRecipient]);
                        setNewHeartbeatRecipient('');
                      }
                    }
                  }}
                  fullWidth
                  placeholder={t('settings.heartbeatRecipients.placeholder') as string}
                  sx={{ mr: 1 }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    if (newHeartbeatRecipient && !heartbeatRecipients.includes(newHeartbeatRecipient)) {
                      setHeartbeatRecipients([...heartbeatRecipients, newHeartbeatRecipient]);
                      setNewHeartbeatRecipient('');
                    }
                  }}
                  disabled={!newHeartbeatRecipient.trim()}
                  sx={{ minWidth: '120px' }}
                >
                  {t('common.add')}
                </Button>
              </Box>

              <Box sx={{ mt: 2 }}>
                {heartbeatRecipients.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.heartbeatRecipients.noRecipients')}
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {heartbeatRecipients.map((email) => (
                      <Chip
                        key={email}
                        label={email}
                        onDelete={() => setHeartbeatRecipients(heartbeatRecipients.filter(r => r !== email))}
                        deleteIcon={<DeleteIcon />}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

    {/* Global Save Button */}
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
      <Button
        variant="contained"
        color="primary"
        size="large"
        startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
        onClick={handleSaveAllSettings}
        disabled={isSaving || !hasUnsavedChanges}
        sx={{ 
          px: 4, 
          py: 1.5,
          fontSize: '1.1rem',
          fontWeight: 'bold',
          boxShadow: hasUnsavedChanges ? 3 : 1,
          '&:hover': {
            boxShadow: hasUnsavedChanges ? 4 : 2,
          }
        }}
      >
        {isSaving ? t('settings.saving') : t('settings.saveAll')}
      </Button>
    </Box>

    {/* Confirmation Dialog */}
    <Dialog
      open={showConfirmDialog}
      onClose={handleNavigationCancel}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
    >
      <DialogTitle id="confirm-dialog-title">
        {t('settings.confirmDialog.saveTitle')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="confirm-dialog-description">
          {t('settings.confirmDialog.saveMessage')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleNavigationCancel} color="primary">
          {t('common.cancel')}
        </Button>
        <Button onClick={handleNavigationConfirm} color="primary">
          {t('settings.confirmDialog.leaveWithoutSaving')}
        </Button>
        <Button 
          onClick={handleSaveAndNavigate} 
          color="primary" 
          variant="contained"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
        >
          {isSaving ? t('settings.saving') : t('settings.confirmDialog.saveAndLeave')}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Success Message */}
    <Snackbar 
      open={saveSuccess} 
      autoHideDuration={4000} 
      onClose={handleCloseSuccessMessage}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert 
        onClose={handleCloseSuccessMessage} 
        severity="success" 
        sx={{ width: '100%' }}
      >
        {t('settings.changes')}
      </Alert>
    </Snackbar>
  </Box>
);
};

export default Settings;